import { useEffect } from 'react';

// Production canonical host. Used to build absolute URLs that Google + social
// platforms can verify and crawl. Stays sleepstack.nl regardless of the
// preview host the visitor happens to be on.
const SITE = 'https://sleepstack.nl';
const DEFAULT_IMAGE = `${SITE}/og-image.jpg`;

/** Update or create a single `<meta>` tag identified by attribute key/value. */
function setMeta(attr, key, content) {
    if (!content) return;
    let el = document.head.querySelector(`meta[${attr}="${key}"]`);
    if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
    }
    el.setAttribute('content', content);
}

/** Inject or update a JSON-LD structured-data script (id-scoped). */
function setJsonLd(data) {
    const id = 'seo-jsonld';
    let el = document.getElementById(id);
    if (!data) {
        if (el) el.remove();
        return;
    }
    if (!el) {
        el = document.createElement('script');
        el.type = 'application/ld+json';
        el.id = id;
        document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(data);
}

/** Update or create the canonical link. */
function setCanonical(href) {
    let el = document.head.querySelector('link[rel="canonical"]');
    if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', 'canonical');
        document.head.appendChild(el);
    }
    el.setAttribute('href', href);
}

/**
 * Per-route SEO + sharing tags.
 *
 * Mutates the existing `<title>`, `<meta name=description>`, `og:*` and
 * `twitter:*` tags from index.html in place so crawlers (both JS-aware
 * and static) only ever see ONE value for each tag — the current route's.
 *
 * Usage:
 *   <SEO title="About Sleepstack" description="..." path="/about" />
 *
 * Anything not provided keeps whatever the previous route or the static
 * baseline set.
 */
export function SEO({ title, description, path = '/', image, type = 'website', jsonLd }) {
    const url = `${SITE}${path}`;
    const img = image
        ? (image.startsWith('http') ? image : `${SITE}${image}`)
        : DEFAULT_IMAGE;

    useEffect(() => {
        if (title) document.title = title;
        if (description) setMeta('name', 'description', description);
        setCanonical(url);

        // Open Graph
        setMeta('property', 'og:type', type);
        if (title) setMeta('property', 'og:title', title);
        if (description) setMeta('property', 'og:description', description);
        setMeta('property', 'og:url', url);
        setMeta('property', 'og:image', img);

        // Twitter / X
        setMeta('name', 'twitter:card', 'summary_large_image');
        if (title) setMeta('name', 'twitter:title', title);
        if (description) setMeta('name', 'twitter:description', description);
        setMeta('name', 'twitter:image', img);

        // Structured data (JSON-LD)
        setJsonLd(jsonLd);
        return () => setJsonLd(null);
    }, [title, description, url, img, type, jsonLd]);

    return null;
}
